/**
 * client/src/lib/use-reconnecting-ws.ts
 *
 * Shared reconnecting-WebSocket primitive (#1667 Task 4, W4 of epic #1663).
 *
 * Extracted from `use-dashboard-ws.ts` (the `dashboard:*` channel-subscribe
 * consumer, migration tracked separately as Task 6 / #1667) so a second
 * consumer with a DIFFERENT on-open handshake shape — `use-comm-push.ts`'s
 * `{ type:"auth", userId, sessionId }` (Task 5) — can share the same
 * reconnect / exponential-backoff / polling-fallback machinery instead of
 * forking it. This module knows NOTHING about `subscribe`/`auth`/channel
 * semantics — only "send this on open" and "does this message pass?".
 *
 * Preserves the original hook's behavior verbatim: BASE_RECONNECT_MS=3_000,
 * MAX_RECONNECT_MS=60_000, MAX_ATTEMPTS=10, exponential backoff via
 * `reconnectDelay`, the `isAttemptingFirst` suppression flag (true until the
 * first successful open), the try/catch around `new WebSocket(...)` that
 * degrades to `isPolling(true)` instead of throwing (this is what keeps the
 * hook alive in an environment with no global `WebSocket`), and clean
 * unmount (clear the pending reconnect timer, close the socket).
 *
 * **Readiness gate (new, needed for the auth-handshake consumer):** the
 * primitive calls `onOpenMessage()` BEFORE constructing a `WebSocket` on
 * every connect attempt (initial and every reconnect). A `null` return means
 * "not ready to connect yet" (e.g. `useCommPush` before the current user has
 * loaded) — the primitive does NOT open a socket, does NOT consume attempt
 * budget, and does NOT trip the polling fallback; it just retries at the
 * fixed `BASE_RECONNECT_MS` interval until `onOpenMessage()` returns a
 * message. Once a connection attempt proceeds, `onOpenMessage()` is called
 * again in the `open` handler (the readiness value may be fresher by then)
 * and its result is sent verbatim if non-null, else nothing is sent this
 * time ("null = send nothing on open", the same meaning it has pre-connect).
 *
 * **Deliberately NOT carried over:** the original hook additionally sent a
 * `{ type:"unsubscribe", channel }` message before closing on unmount — that
 * is `dashboard:*`-specific vocabulary, not a generic WebSocket concern, so
 * it stays out of the primitive. `use-dashboard-ws.ts`'s own migration
 * (Task 6, deferrable) either fits this shape or names precisely why not.
 *
 * **`resetKey` (identity-change reconnect, Task 4 follow-up):** the primitive
 * reads `onOpenMessage`/`messageFilter`/`onMessage` through refs and connects
 * once on mount, so on its own it never notices that the *identity* behind
 * those callbacks changed — e.g. the org context or the authenticated user.
 * `resetKey` is an optional 4th param, a plain string the caller derives from
 * whatever value SHOULD force a fresh socket. When it changes (by `!==`,
 * checked after mount — the initial render never counts as a "change"): the
 * current socket is closed, any pending reconnect timer is cleared, the
 * attempt counter resets to 0 (a new identity gets its own full retry budget
 * rather than inheriting the old identity's exhaustion), `isAttemptingFirst`
 * resets to `true`, and `connect()` runs again immediately. Omitting
 * `resetKey` (or passing a value that never changes) is fully
 * backward-compatible — the hook behaves exactly as it did before this
 * param existed. **Callers:** `use-comm-push.ts` (Task 5) should pass the
 * current user id — a changed `userId` must not keep sending frames under
 * the OLD identity's handshake, since the server's broadcast registry keys
 * on the cookie identity captured at handshake time. `use-dashboard-ws.ts`
 * (Task 6) should pass the channel name (`dashboard:<orgId>` /
 * `dashboard:platform`) — the original hook scoped its own `connect` to
 * `[channel, eventFilter]` precisely so switching org context tore down and
 * reopened on the new channel; `resetKey` is how the shared primitive
 * reproduces that.
 */

import { useCallback, useEffect, useRef, useState } from "react";

const BASE_RECONNECT_MS = 3_000;
const MAX_RECONNECT_MS = 60_000;
const MAX_ATTEMPTS = 10;

export interface UseReconnectingWsResult {
  readonly isConnected: boolean;
  readonly isPolling: boolean;
  readonly isAttemptingFirst: boolean;
}

/** Computes exponential back-off delay capped at MAX_RECONNECT_MS. */
function reconnectDelay(attempt: number): number {
  return Math.min(BASE_RECONNECT_MS * Math.pow(2, attempt), MAX_RECONNECT_MS);
}

/** Builds the `/ws` endpoint URL for the current origin (http->ws, https->wss). */
function wsUrl(): string {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/ws`;
}

/**
 * Opens a reconnecting `/ws` WebSocket, sends an on-open handshake built by
 * the caller, and forwards messages that pass a caller-supplied filter.
 *
 * @param onOpenMessage Called fresh on every connect attempt (and again on
 *   `open`). Returning `null` means "not ready to connect" pre-connect, or
 *   "send nothing this open" once the socket is live.
 * @param messageFilter Receives every parsed JSON-object frame; return
 *   `true` to forward it to `onMessage`. Non-object / malformed frames never
 *   reach this filter.
 * @param onMessage Called with each frame that passes `messageFilter`.
 * @param resetKey Optional. When this value changes (after mount), the
 *   current socket is torn down, the attempt budget resets to 0, and a fresh
 *   `connect()` runs — see the `resetKey` section in the module doc above.
 *   Omit it (or pass a value that never changes) for the pre-existing
 *   behavior.
 */
export function useReconnectingWs<T extends Record<string, unknown>>(
  onOpenMessage: () => Record<string, unknown> | null,
  messageFilter: (msg: Record<string, unknown>) => boolean,
  onMessage: (data: T) => void,
  resetKey?: string,
): UseReconnectingWsResult {
  const [isConnected, setIsConnected] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  // True until the first successful open — suppresses reconnect UI on load.
  const [isAttemptingFirst, setIsAttemptingFirst] = useState(true);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptRef = useRef(0);
  // Prevents double reconnect from simultaneous error+close events.
  const suppressReconnectRef = useRef(false);

  const onOpenMessageRef = useRef(onOpenMessage);
  onOpenMessageRef.current = onOpenMessage;
  const messageFilterRef = useRef(messageFilter);
  messageFilterRef.current = messageFilter;
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const connect = useCallback((): void => {
    if (attemptRef.current >= MAX_ATTEMPTS) {
      setIsPolling(true);
      return;
    }

    const handshake = onOpenMessageRef.current();
    if (handshake === null) {
      // Not ready yet (e.g. no authenticated user) — retry at the base
      // interval without consuming attempt budget or tripping polling.
      reconnectTimerRef.current = setTimeout(connect, BASE_RECONNECT_MS);
      return;
    }

    try {
      const ws = new WebSocket(wsUrl());
      wsRef.current = ws;
      suppressReconnectRef.current = false;

      ws.addEventListener("open", () => {
        setIsConnected(true);
        setIsPolling(false);
        setIsAttemptingFirst(false);
        attemptRef.current = 0;
        const freshHandshake = onOpenMessageRef.current();
        if (freshHandshake !== null) ws.send(JSON.stringify(freshHandshake));
      });

      ws.addEventListener("message", (evt) => {
        try {
          const raw: unknown = JSON.parse(evt.data);
          if (typeof raw !== "object" || raw === null) return;
          const msg = raw as Record<string, unknown>;
          if (messageFilterRef.current(msg)) onMessageRef.current(msg as T);
        } catch { /* ignore malformed frames */ }
      });

      ws.addEventListener("close", () => {
        // Generation guard: a real WebSocket.close() dispatches its `close`
        // event on a LATER TASK, never synchronously — so by the time a
        // superseded socket's close event fires, `resetKey`'s connect() may
        // already have installed a NEW socket in wsRef. Without this check
        // the stale handler would null out the reference to the live socket
        // and schedule a spurious extra reconnect on top of it (orphaning
        // the live socket — see the module doc's resetKey race note).
        if (wsRef.current !== ws) return;
        setIsConnected(false);
        wsRef.current = null;
        if (suppressReconnectRef.current) return;
        suppressReconnectRef.current = true;
        const delay = reconnectDelay(attemptRef.current);
        attemptRef.current += 1;
        reconnectTimerRef.current = setTimeout(() => {
          suppressReconnectRef.current = false;
          connect();
        }, delay);
      });

      ws.addEventListener("error", () => {
        // Same generation guard as `close` — a superseded socket's error
        // event must not touch the current (possibly already-replaced)
        // wsRef or schedule a reconnect on its behalf.
        if (wsRef.current !== ws) return;
        suppressReconnectRef.current = true;
        setIsPolling(true);
        ws.close();
        const delay = reconnectDelay(attemptRef.current);
        attemptRef.current += 1;
        reconnectTimerRef.current = setTimeout(() => {
          suppressReconnectRef.current = false;
          connect();
        }, delay);
      });
    } catch {
      setIsPolling(true);
    }
  }, []);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [connect]);

  // Tracks whether the mount effect above has already performed the initial
  // connect for the current resetKey — skips this effect's first run so a
  // fresh mount never double-connects.
  const hasMountedRef = useRef(false);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    // resetKey changed post-mount: this is a fresh identity, not a
    // reconnect attempt — tear down and start over with a full budget.
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    suppressReconnectRef.current = true;
    if (wsRef.current) {
      // Close the old socket even though its close/error handlers are now
      // generation-guarded no-ops (wsRef.current will already point at the
      // new socket, or null, by the time the old socket's close event
      // eventually fires) — closing it still matters so the browser
      // actually tears down the old connection instead of leaking it.
      wsRef.current.close();
      wsRef.current = null;
    }
    attemptRef.current = 0;
    setIsConnected(false);
    setIsPolling(false);
    setIsAttemptingFirst(true);
    connect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  return { isConnected, isPolling, isAttemptingFirst };
}
